import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Trash2 } from 'lucide-react';
import { useComments, useCreateComment, useDeleteComment } from '@/hooks/useComments';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';

const MAX_CHARS = 500;

interface Props {
  candidateId: string;
  currentUser: string;
}

export function CommentSection({ candidateId, currentUser }: Props) {
  const [text, setText] = useState('');
  const { data: comments, isLoading } = useComments(candidateId);
  const createComment = useCreateComment();
  const deleteComment = useDeleteComment();

  function handleSubmit() {
    if (!text.trim() || text.length > MAX_CHARS) return;
    createComment.mutate(
      { candidate_id: candidateId, text: text.trim(), author: currentUser },
      { onSuccess: () => setText('') },
    );
  }

  const over = text.length > MAX_CHARS;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Adicionar comentario..."
          rows={3}
          className={over ? 'border-status-red focus:ring-status-red' : ''}
        />
        <div className="flex items-center justify-between">
          <span
            className={`text-xs ${
              over ? 'text-status-red' : 'text-text-muted'
            }`}
          >
            {text.length}/{MAX_CHARS}
          </span>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={!text.trim() || over || createComment.isPending}
          >
            {createComment.isPending ? 'Salvando...' : 'Comentar'}
          </Button>
        </div>
      </div>

      {isLoading && (
        <p className="text-xs text-text-muted">Carregando comentarios...</p>
      )}

      <div className="flex flex-col gap-3">
        {comments?.map((comment) => (
          <div
            key={comment.id}
            className="rounded-lg border border-border bg-bg-base p-3 group"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-text-primary">
                  {comment.author}
                </span>
                <span className="text-xs text-text-muted">
                  {comment.created_at
                    ? format(parseISO(comment.created_at), "d MMM yyyy, HH:mm", {
                        locale: ptBR,
                      })
                    : '—'}
                </span>
              </div>
              {comment.author === currentUser && (
                <button
                  onClick={() =>
                    deleteComment.mutate({
                      candidateId,
                      commentId: comment.id,
                    })
                  }
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-text-muted hover:text-status-red"
                  title="Excluir comentario"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            <p className="mt-1.5 text-sm text-text-secondary whitespace-pre-wrap">
              {comment.text}
            </p>
          </div>
        ))}
        {comments?.length === 0 && (
          <p className="text-xs text-text-muted text-center py-4">
            Nenhum comentario ainda.
          </p>
        )}
      </div>
    </div>
  );
}
